// 登录态：token 存 localStorage，启动时用 /auth/me 校验；401 由 api 层清 token 后回登录页
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { UserDTO } from "@csp/shared";
import { api, getToken, setToken, setUnauthorizedHandler } from "./api";

interface AuthState {
  user: UserDTO | null;
  token: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children, onUnauthorized }: { children: ReactNode; onUnauthorized?: () => void }) {
  const [user, setUser] = useState<UserDTO | null>(null);
  const [token, setTok] = useState<string | null>(getToken());
  const [loading, setLoading] = useState<boolean>(!!getToken());

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setTok(null);
      setUser(null);
      onUnauthorized?.();
    });
    return () => setUnauthorizedHandler(null);
  }, [onUnauthorized]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let alive = true;
    api
      .me()
      .then((u) => alive && setUser(u))
      .catch(() => alive && setUser(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token]);

  const login = useCallback(async (username: string, password: string) => {
    const r = await api.login(username, password);
    setToken(r.token);
    setUser(r.user);
    setTok(r.token);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* 已失效也照样退出 */
    }
    setToken(null);
    setTok(null);
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, token, loading, login, logout }), [user, token, loading, login, logout]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth 必须在 AuthProvider 里用");
  return v;
}

/** 已登录用户（只在登录后的页面里用） */
export function useUser(): UserDTO {
  const { user } = useAuth();
  if (!user) throw new Error("未登录");
  return user;
}

export function isStaff(role: string | undefined): boolean {
  return role === "clerk" || role === "admin";
}
